export type FusionTextState = 'available' | 'missing' | 'plain'

export interface FusionTextPart {
  text: string
  state: FusionTextState
}

/** Quoted material names in a fusion description, in printed order. */
export function quotedMaterialNames(desc: string): string[] {
  return [...desc.matchAll(/"([^"]+)"/g)]
    .map((match) => match[1] ?? '')
    .filter(Boolean)
}

/**
 * True when every quoted material is present in `available`, consuming one
 * copy per quote (a fusion that lists the same monster twice needs two).
 */
export function materialsCovered(
  needed: readonly string[],
  available: readonly string[],
): boolean {
  if (needed.length < 2) return false
  const remaining = [...available]
  for (const name of needed) {
    const idx = remaining.indexOf(name)
    if (idx === -1) return false
    remaining.splice(idx, 1)
  }
  return true
}

/**
 * Split a fusion description so each quoted material can be colored.
 * `availableNames` must already be in the same language as `desc`.
 */
export function splitFusionText(
  desc: string,
  availableNames: readonly string[],
): FusionTextPart[] {
  const parts: FusionTextPart[] = []
  const remaining = [...availableNames]
  const regex = /"([^"]+)"/g
  let cursor = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(desc)) !== null) {
    if (match.index > cursor) {
      parts.push({ text: desc.slice(cursor, match.index), state: 'plain' })
    }
    const materialName = match[1] ?? ''
    const idx = remaining.indexOf(materialName)
    const available = idx !== -1
    if (available) remaining.splice(idx, 1)
    parts.push({
      text: `"${materialName}"`,
      state: available ? 'available' : 'missing',
    })
    cursor = match.index + match[0].length
  }

  if (cursor < desc.length) {
    parts.push({ text: desc.slice(cursor), state: 'plain' })
  }

  return parts.length > 0 ? parts : [{ text: desc, state: 'plain' }]
}

export function fusionMaterialsReady(parts: readonly FusionTextPart[]): boolean {
  const materials = parts.filter((part) => part.state !== 'plain')
  return materials.length >= 2 && materials.every((part) => part.state === 'available')
}
